/**
 * Task text syntax, compatible with the Obsidian Tasks plugin (emoji format)
 * and the Reminder plugin: `- [ ] Offerte versturen 📅 2026-10-05 ⏫ 🔁 every week`.
 * Pure string logic: used by the editor, the indexer and the task views.
 */
import type { TaskPriority, TaskStatus } from './model';

export type IsoDate = string; // YYYY-MM-DD
export type IsoDateTime = string; // YYYY-MM-DD or YYYY-MM-DDTHH:mm

export interface ParsedTask {
  /** Description without the metadata tokens. */
  title: string;
  due: IsoDate | null;
  /** Time of the due date ("14:00"), from `📅 2026-10-05 14:00`. */
  dueTime: string | null;
  scheduled: IsoDate | null;
  start: IsoDate | null;
  created: IsoDate | null;
  doneAt: IsoDate | null;
  cancelledAt: IsoDate | null;
  priority: TaskPriority | null;
  recurrence: string | null;
  reminders: IsoDateTime[];
  /** `⏰ -1d`, `⏰ 30m`: minutes before the due date/time. */
  relativeReminders: number[];
  tags: string[];
}

const DATE = '(\\d{4}-\\d{2}-\\d{2})';
const DATE_FIELDS = {
  due: '📅',
  scheduled: '⏳',
  start: '🛫',
  created: '➕',
  doneAt: '✅',
  cancelledAt: '❌',
} as const;
type DateField = keyof typeof DATE_FIELDS;

export const PRIORITY_EMOJI: Record<TaskPriority, string> = {
  highest: '🔺',
  high: '⏫',
  medium: '🔼',
  low: '🔽',
  lowest: '⏬',
};
const EMOJI_PRIORITY = Object.fromEntries(
  Object.entries(PRIORITY_EMOJI).map(([k, v]) => [v, k as TaskPriority]),
) as Record<string, TaskPriority>;

const TOKEN_STARTS = [
  '📅',
  '⏳',
  '🛫',
  '➕',
  '✅',
  '❌',
  '🔁',
  '⏰',
  '🆔',
  '⛔',
  ...Object.values(PRIORITY_EMOJI),
];
const VS16 = `${String.fromCharCode(0xfe0f)}?`; // emoji variation selector some editors add
// Recurrence text runs until the next token, tag or link.
const RECUR_RE = new RegExp(`🔁${VS16}\\s*([^${TOKEN_STARTS.join('')}#[]+)`, 'u');
// Date and time may be separated by any space: browsers often type a no-break space.
const REMINDER_EMOJI_RE = new RegExp(`⏰${VS16}\\s*${DATE}(?:[\\sT](\\d{1,2}:\\d{2}))?`, 'gu');
const REMINDER_PLUGIN_RE = /\(@(\d{4}-\d{2}-\d{2})(?:[\sT](\d{1,2}:\d{2}))?\)/g;
const REMINDER_RELATIVE_RE = new RegExp(
  `⏰${VS16}\\s*-?(\\d+)\\s*(min|m|h|u|d|w)(?![\\p{L}\\d])`,
  'gu',
);
const UNIT_MINUTES: Record<string, number> = { m: 1, min: 1, h: 60, u: 60, d: 1440, w: 10080 };
const TAG_RE = /(?:^|\s)#([\p{L}\p{N}_\-/]*[^\d\s/#][\p{L}\p{N}_\-/]*)/gu;

const pad = (n: number) => String(n).padStart(2, '0');
/** Optional time after a date; any space (browsers type no-break spaces) or "T". */
const TIME_SUFFIX = '(?:[\\sT](\\d{1,2}:\\d{2}))?';
/** "9:5" / "9:05" → "09:05". */
export const normalTime = (t: string) =>
  `${pad(Number(t.split(':')[0]))}:${pad(Number(t.split(':')[1] ?? 0))}`;
const time = (t: string | undefined) => (t ? `T${normalTime(t)}` : '');

/** The value of a `📅` token: "2026-10-05" or "2026-10-05 14:00". */
export const dueToken = (date: IsoDate, dueTime?: string | null) =>
  dueTime ? `${date} ${normalTime(dueTime)}` : date;

/**
 * A date with an optional time, as typed or stored: "2026-10-05", "2026-10-05 14:00",
 * "2026-10-05T14:00" (seconds or a zone after it are ignored).
 */
export function splitDateTime(value: string): { date: IsoDate; time: string | null } | null {
  const m = new RegExp(`^${DATE}${TIME_SUFFIX}`).exec(value.trim());
  return m ? { date: m[1]!, time: m[2] ? normalTime(m[2]) : null } : null;
}

export function parseTaskText(text: string): ParsedTask {
  let rest = text;
  const result: ParsedTask = {
    title: '',
    due: null,
    dueTime: null,
    scheduled: null,
    start: null,
    created: null,
    doneAt: null,
    cancelledAt: null,
    priority: null,
    recurrence: null,
    reminders: [],
    relativeReminders: [],
    tags: [],
  };
  for (const [field, emoji] of Object.entries(DATE_FIELDS) as [DateField, string][]) {
    // The due date may carry a time: `📅 2026-10-05 14:00`.
    const re = new RegExp(`${emoji}${VS16}\\s*${DATE}${field === 'due' ? TIME_SUFFIX : ''}`, 'u');
    const m = re.exec(rest);
    if (m) {
      result[field] = m[1]!;
      if (field === 'due' && m[2]) result.dueTime = normalTime(m[2]);
      rest = rest.replace(re, ' ');
    }
  }
  for (const [emoji, priority] of Object.entries(EMOJI_PRIORITY)) {
    if (rest.includes(emoji)) {
      result.priority ??= priority;
      rest = rest.split(emoji).join(' ');
    }
  }
  const recur = RECUR_RE.exec(rest);
  if (recur) {
    result.recurrence = recur[1]!.trim();
    rest = rest.replace(RECUR_RE, ' ');
  }
  rest = rest.replace(REMINDER_RELATIVE_RE, (_m, n: string, unit: string) => {
    result.relativeReminders.push(Number(n) * UNIT_MINUTES[unit]!);
    return ' ';
  });
  for (const re of [REMINDER_EMOJI_RE, REMINDER_PLUGIN_RE]) {
    rest = rest.replace(re, (_m, d: string, t?: string) => {
      result.reminders.push(`${d}${time(t)}`);
      return ' ';
    });
  }
  for (const m of text.matchAll(TAG_RE)) result.tags.push(m[1]!.replace(/\/+$/, ''));
  result.title = rest
    .replace(/[🆔⛔]\S*\s*\S+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return result;
}

// ---- status -------------------------------------------------------------------------

/** Checkbox character → status (Obsidian Tasks conventions). */
export function statusFromChar(ch: string): TaskStatus {
  if (ch === 'x' || ch === 'X') return 'done';
  if (ch === '-') return 'cancelled';
  if (ch === '/') return 'doing';
  if (ch === '?' || ch === '!' || ch === '<') return 'waiting';
  return 'todo';
}

export const STATUS_CHAR: Record<TaskStatus, string> = {
  todo: ' ',
  doing: '/',
  waiting: '?',
  done: 'x',
  cancelled: '-',
};

const STATUS_WORDS: [TaskStatus, string[]][] = [
  [
    'done',
    [
      'done',
      'klaar',
      'opgelost',
      'afgerond',
      'gedaan',
      'completed',
      'complete',
      'closed',
      'gesloten',
    ],
  ],
  ['cancelled', ['cancelled', 'canceled', 'geannuleerd', 'afgebroken', 'vervallen']],
  ['doing', ['in progress', 'bezig', 'lopend', 'doing', 'started', 'gestart', 'actief']],
  ['waiting', ['on hold', 'wacht', 'wachten', 'waiting', 'geblokkeerd', 'blocked', 'on-hold']],
  [
    'todo',
    ['to do', 'todo', 'open', 'te doen', 'nieuw', 'new', 'not started', 'backlog', 'gepland'],
  ],
];

/** Free-text status property (as used in the vault) → status. Unknown values count as todo. */
export function statusFromWord(value: unknown): TaskStatus {
  const v = String(value ?? '')
    .toLowerCase()
    .trim();
  for (const [status, words] of STATUS_WORDS)
    if (words.some((w) => v === w || v.startsWith(`${w} `))) return status;
  for (const [status, words] of STATUS_WORDS) if (words.some((w) => v.includes(w))) return status;
  return 'todo';
}

/** Status property values Nexus writes (matching the vault's existing vocabulary). */
export const STATUS_WORD: Record<TaskStatus, string> = {
  todo: 'To do',
  doing: 'In progress',
  waiting: 'On Hold',
  done: 'Done',
  cancelled: 'Cancelled',
};

export function priorityFromWord(value: unknown): TaskPriority | null {
  const v = String(value ?? '')
    .toLowerCase()
    .trim();
  if (!v) return null;
  if (/^(highest|hoogste|urgent|kritiek|critical|p0)$/.test(v)) return 'highest';
  if (/^(high|hoog|p1|belangrijk)$/.test(v)) return 'high';
  if (/^(medium|normaal|middel|gemiddeld|p2)$/.test(v)) return 'medium';
  if (/^(low|laag|p3)$/.test(v)) return 'low';
  if (/^(lowest|laagste|p4)$/.test(v)) return 'lowest';
  return null;
}

export const PRIORITY_WORD: Record<TaskPriority, string> = {
  highest: 'Highest',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  lowest: 'Lowest',
};

// ---- editing task text ----------------------------------------------------------------

/** Remove a token (`📅 2026-10-05`, `⏫`, …) from task text. */
export function removeToken(text: string, emoji: string): string {
  const re =
    emoji in EMOJI_PRIORITY
      ? new RegExp(`\\s*${emoji}${VS16}`, 'gu')
      : emoji === '🔁'
        ? new RegExp(`\\s*${RECUR_RE.source}`, 'gu')
        : new RegExp(`\\s*${emoji}${VS16}\\s*${DATE}(?:[\\sT]\\d{1,2}:\\d{2})?`, 'gu');
  return text.replace(re, '');
}

/** Set (or with `null` remove) a token, appended at the end like the Tasks plugin does. */
export function setToken(text: string, emoji: string, value: string | null): string {
  const base = removeToken(text, emoji).replace(/\s+$/, '');
  if (value === null) return base;
  return `${base} ${emoji}${value ? ` ${value}` : ''}`;
}

export function setPriorityToken(text: string, priority: TaskPriority | null): string {
  let out = text;
  for (const emoji of Object.values(PRIORITY_EMOJI)) out = removeToken(out, emoji);
  return priority ? setToken(out, PRIORITY_EMOJI[priority], '') : out.replace(/\s+$/, '');
}

// ---- dates -------------------------------------------------------------------------------

export function toIsoDate(d: Date): IsoDate {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fromIsoDate(s: IsoDate): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = fromIsoDate(date);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

function addMonths(date: IsoDate, months: number): IsoDate {
  const d = fromIsoDate(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return toIsoDate(d);
}

const WEEKDAYS: Record<string, number> = {
  sunday: 0,
  zondag: 0,
  zo: 0,
  sun: 0,
  monday: 1,
  maandag: 1,
  ma: 1,
  mon: 1,
  tuesday: 2,
  dinsdag: 2,
  di: 2,
  tue: 2,
  wednesday: 3,
  woensdag: 3,
  wo: 3,
  wed: 3,
  thursday: 4,
  donderdag: 4,
  do: 4,
  thu: 4,
  friday: 5,
  vrijdag: 5,
  vr: 5,
  fri: 5,
  saturday: 6,
  zaterdag: 6,
  za: 6,
  sat: 6,
};

/** Next date strictly after `from` falling on `weekday`. */
function nextWeekday(from: IsoDate, weekday: number): IsoDate {
  const d = fromIsoDate(from);
  const diff = (weekday - d.getDay() + 7) % 7 || 7;
  return addDays(from, diff);
}

/**
 * Next occurrence for a recurrence rule ("every week", "every 2 days", "elke maandag",
 * "every month", "iedere 3 weken"). Returns null for rules it does not understand.
 */
/** Single words for a rule: "dagelijks", "weekly", … */
const RULE_WORDS: Record<string, string> = {
  dagelijks: 'every day',
  daily: 'every day',
  wekelijks: 'every week',
  weekly: 'every week',
  tweewekelijks: 'every 2 weeks',
  biweekly: 'every 2 weeks',
  maandelijks: 'every month',
  monthly: 'every month',
  driemaandelijks: 'every 3 months',
  kwartaal: 'every 3 months',
  jaarlijks: 'every year',
  yearly: 'every year',
  werkdagen: 'every weekday',
  weekdays: 'every weekday',
};

export function nextOccurrence(rule: string, from: IsoDate): IsoDate | null {
  const cleaned = rule
    .toLowerCase()
    .replace(/\s+when done$|\s+als klaar$/, '')
    .trim();
  const r = RULE_WORDS[cleaned] ?? cleaned;
  const m =
    /^(?:every|elke|iedere|ieder)\s+(?:(\d+)\s+)?(day|days|dag|dagen|week|weeks|weken|month|months|maand|maanden|year|years|jaar|jaren|weekday|werkdag)$/.exec(
      r,
    );
  if (m) {
    const n = Number(m[1] ?? 1);
    const unit = m[2]!;
    if (/^(day|days|dag|dagen)$/.test(unit)) return addDays(from, n);
    if (/^(week|weeks|weken)$/.test(unit)) return addDays(from, 7 * n);
    if (/^(month|months|maand|maanden)$/.test(unit)) return addMonths(from, n);
    if (/^(year|years|jaar|jaren)$/.test(unit)) return addMonths(from, 12 * n);
    let next = addDays(from, 1);
    while ([0, 6].includes(fromIsoDate(next).getDay())) next = addDays(next, 1);
    return next;
  }
  const w = /^(?:every|elke|iedere|ieder)\s+([a-z]+)$/.exec(r);
  if (w && w[1]! in WEEKDAYS) return nextWeekday(from, WEEKDAYS[w[1]!]!);
  return null;
}

/** The recurrence choices offered in the Herhaling dropdown (all understood by nextOccurrence). */
export const RECURRENCE_PRESETS = [
  'dagelijks',
  'werkdagen',
  'wekelijks',
  'tweewekelijks',
  'maandelijks',
  'driemaandelijks',
  'jaarlijks',
];

/** Whether Nexus understands a recurrence rule ("elke maandag", "elke 2 weken als klaar"). */
export const isRecurrenceRule = (rule: string): boolean =>
  !!rule.trim() && nextOccurrence(rule, '2026-01-01') !== null;

// ---- quick capture ----------------------------------------------------------------------

/** Time after a capture date: "14:00", "14u", "14u30", "9h", optionally after "om"/"at". */
const CAPTURE_TIME = '(?:\\s+(?:(?:om|at)\\s+)?(\\d{1,2})(?::(\\d{2})|[uh](\\d{2})?))?';

/**
 * Quick-capture shorthand → task text: `@morgen`, `@vrijdag`, `@12/10`, `@2026-10-05`,
 * `@over 3 dagen` become `📅 date`, with an optional time (`@morgen 14:00`, `@vrijdag om 9u`);
 * `!!`/`!hoog`, `!`/`!normaal`, `!laag` a priority.
 */
export function expandCapture(input: string, today: IsoDate): string {
  let text = input;
  let due: IsoDate | null = null;
  let dueTime: string | null = null;
  let priority: TaskPriority | null = null;
  const setTime = (h?: string, m1?: string, m2?: string) => {
    if (h !== undefined && Number(h) < 24) dueTime = normalTime(`${h}:${m1 ?? m2 ?? '00'}`);
  };

  text = text.replace(
    new RegExp(
      `(?:^|\\s)@(?:over\\s+(\\d+)\\s+(dag|dagen|week|weken))${CAPTURE_TIME}(?=\\s|$)`,
      'i',
    ),
    (_m, n: string, unit: string, h?: string, m1?: string, m2?: string) => {
      due = addDays(today, Number(n) * (/^w/i.test(unit) ? 7 : 1));
      setTime(h, m1, m2);
      return ' ';
    },
  );
  const dateWord = new RegExp(`(?:^|\\s)@([\\p{L}\\d/\\-.]+)${CAPTURE_TIME}(?=\\s|$)`, 'iu');
  text = text.replace(dateWord, (m, word: string, h?: string, m1?: string, m2?: string) => {
    const w = word.toLowerCase();
    let date: IsoDate | null = null;
    if (w === 'vandaag' || w === 'today') date = today;
    else if (w === 'morgen' || w === 'tomorrow') date = addDays(today, 1);
    else if (w === 'overmorgen') date = addDays(today, 2);
    else if (w === 'volgendeweek' || w === 'nextweek') date = addDays(today, 7);
    else if (w in WEEKDAYS) date = nextWeekday(today, WEEKDAYS[w]!);
    else if (/^\d{4}-\d{2}-\d{2}$/.test(w)) date = w;
    else {
      const dm = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(w);
      if (dm) {
        const year = dm[3]
          ? Number(dm[3].length === 2 ? `20${dm[3]}` : dm[3])
          : Number(today.slice(0, 4));
        let candidate = `${year}-${pad(Number(dm[2]))}-${pad(Number(dm[1]))}`;
        if (!dm[3] && candidate < today) candidate = `${year + 1}${candidate.slice(4)}`;
        date = candidate;
      }
    }
    if (!date) return m;
    if (!due) {
      due = date;
      setTime(h, m1, m2);
    }
    return ' ';
  });
  text = text.replace(
    /(?:^|\s)(!!|!hoog|!high|!urgent|!laag|!low|!normaal|!medium|!)(?=\s|$)/i,
    (_m, p: string) => {
      const v = p.toLowerCase();
      priority =
        v === '!!' || v === '!hoog' || v === '!high' || v === '!urgent'
          ? 'high'
          : v === '!laag' || v === '!low'
            ? 'low'
            : 'medium';
      return ' ';
    },
  );
  text = text.replace(/\s+/g, ' ').trim();
  if (due) text = setToken(text, DATE_FIELDS.due, dueToken(due, dueTime));
  if (priority) text = setPriorityToken(text, priority);
  return text;
}

/** Tasks that count as open (not done or cancelled). */
export const isOpen = (status: TaskStatus) => status !== 'done' && status !== 'cancelled';
